import is from '@sindresorhus/is';
import deepEqual from 'fast-deep-equal';
import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
    DefaultHardSoftProficiencyWithUnknownLevel,
    DefaultLanguageProficiencyWithUnknownLevel,
    skillShortSchema,
} from '../../../../../../shared/source/data-contract/skill';
import { useSearchSkillsQuery } from '../../../query-hooks/skills';

import type { BaseSkill, SkillShort } from '../../../../../../shared/source/data-contract/skill';
import type { FieldMetaProps } from 'formik';

const defaultSkillSearchLimit = 10;

const mapSkillToDefaultValues = (skill: SkillShort): SkillShort =>
    skill.skillType === 'language'
        ? {
              ...skill,
              level:
                  !is.nullOrUndefined(skill.level) && skill.level > 0
                      ? skill.level
                      : DefaultLanguageProficiencyWithUnknownLevel,
          }
        : {
              ...skill,
              level:
                  !is.nullOrUndefined(skill.level) && skill.level > 0
                      ? skill.level
                      : DefaultHardSoftProficiencyWithUnknownLevel,
          };

type UseSkillsField = {
    readonly addSkill: (skillToAdd: SkillShort) => void;
    readonly addSkills: (skillsToAdd: readonly SkillShort[]) => void;
    readonly changeSkillProficiencyLevel: (skillId: string, newProficiency: SkillShort['level']) => void;
    readonly existingSkills: readonly SkillShort[];
    readonly isSkillOnBlackList: (skill: SkillShort) => boolean;
    readonly meta: FieldMetaProps<readonly SkillShort[]>;
    readonly removeSkill: (removedSkillId: string) => void;
    readonly reorderSkills: (updatedSkills: readonly SkillShort[]) => void;
    readonly setError: (error: string) => void;
};

export const useSkillsField = (): UseSkillsField => {
    const [blackListedSkills, setBlackListedSkills] = useState<readonly string[]>([]);
    const [{ value: existingSkills }, meta, helper] = useField<readonly SkillShort[]>('skills');

    const setSkillsToField = (skills: readonly SkillShort[]): void => {
        helper.setValue(skills, true);
        helper.setTouched(true);
    };

    const addSkills: UseSkillsField['addSkills'] = (skillsToAdd) => {
        const mappedSkillsToAdd = skillsToAdd.map(mapSkillToDefaultValues);
        const uniqueSkillsToSet = [...new Set([...existingSkills, ...mappedSkillsToAdd])];

        if (deepEqual(existingSkills, uniqueSkillsToSet)) {
            return;
        }

        setSkillsToField(uniqueSkillsToSet);
    };

    return {
        addSkill(skillToAdd) {
            addSkills([skillToAdd]);
        },
        addSkills,
        changeSkillProficiencyLevel(skillId, newProficiencyLevel) {
            const updatedSkills = existingSkills.map((skillInField) =>
                skillInField.id === skillId
                    ? skillShortSchema.parse({
                          ...skillInField,
                          level: newProficiencyLevel,
                      })
                    : skillInField,
            );
            setSkillsToField(updatedSkills);
        },
        existingSkills,
        isSkillOnBlackList(skill) {
            return blackListedSkills.includes(skill.id);
        },
        meta,
        removeSkill(removedSkillId) {
            setBlackListedSkills([...blackListedSkills, removedSkillId]);
            const updatedSkills = existingSkills.filter(({ id }) => id !== removedSkillId);

            setSkillsToField(updatedSkills);
        },
        reorderSkills(updatedSkills) {
            const draggedSkillSectionType: ReadonlyArray<SkillShort['skillType']> =
                updatedSkills[0]?.skillType === 'language' ? ['language'] : ['hard', 'soft'];

            const untouchedSkillsSection = existingSkills.filter(
                ({ skillType }) => !draggedSkillSectionType.includes(skillType),
            );

            setSkillsToField([...updatedSkills, ...untouchedSkillsSection]);
        },
        setError(error: string) {
            helper.setTouched(true, false);
            helper.setError(error);
        },
    };
};

type UseSkillsFieldReactHookForm = Omit<UseSkillsField, 'meta'>;

export const useSkillsFieldReactHookForm = (): UseSkillsFieldReactHookForm => {
    const [blackListedSkills, setBlackListedSkills] = useState<readonly string[]>([]);
    const { setError, setValue, watch } = useFormContext();

    const existingSkills: readonly SkillShort[] = watch('skills', []);

    const setSkills = (skills: readonly SkillShort[]): void => {
        setValue('skills', skills);
    };
    const setSkillsToField = (skills: readonly SkillShort[]): void => {
        setSkills(skills);
    };

    const addSkills: UseSkillsField['addSkills'] = (skillsToAdd) => {
        const mappedSkillsToAdd = skillsToAdd.map(mapSkillToDefaultValues);
        const uniqueSkillsToSet = [...new Set([...existingSkills, ...mappedSkillsToAdd])];

        if (deepEqual(existingSkills, uniqueSkillsToSet)) {
            return;
        }

        setSkillsToField(uniqueSkillsToSet);
    };

    return {
        addSkill(skillToAdd) {
            addSkills([skillToAdd]);
        },
        addSkills,
        changeSkillProficiencyLevel(skillId, newProficiencyLevel) {
            const updatedSkills = existingSkills.map((skillInField) =>
                skillInField.id === skillId
                    ? skillShortSchema.parse({
                          ...skillInField,
                          level: newProficiencyLevel,
                      })
                    : skillInField,
            );
            setSkillsToField(updatedSkills);
        },
        existingSkills,
        isSkillOnBlackList(skill) {
            return blackListedSkills.includes(skill.id);
        },
        removeSkill(removedSkillId) {
            setBlackListedSkills([...blackListedSkills, removedSkillId]);
            const updatedSkills = existingSkills.filter(({ id }) => id !== removedSkillId);

            setSkillsToField(updatedSkills);
        },
        reorderSkills(updatedSkills) {
            const draggedSkillSectionType: ReadonlyArray<SkillShort['skillType']> =
                updatedSkills[0]?.skillType === 'language' ? ['language'] : ['hard', 'soft'];

            const untouchedSkillsSection = existingSkills.filter(
                ({ skillType }) => !draggedSkillSectionType.includes(skillType),
            );

            setSkillsToField([...updatedSkills, ...untouchedSkillsSection]);
        },
        setError(error: string) {
            setError('skills', { message: error });
        },
    };
};

export const filterSkills = <T extends BaseSkill, S extends BaseSkill>(
    skills: readonly T[],
    excludedSkills: readonly S[],
): readonly T[] => {
    const excludedIds = excludedSkills.map(({ id }) => id);

    return skills.filter((skill) => !excludedIds.includes(skill.id));
};

export const useSearchSkillsQueryWithLimit = (
    searchTerm: string,
    skillSearchLimit: number,
): {
    readonly hasMoreSkills: boolean;
    readonly hasResults: boolean;
    readonly isFetching: boolean;
    readonly skills: readonly SkillShort[];
} => {
    const skillLimitWithPreFetchingNextItems = skillSearchLimit + defaultSkillSearchLimit;
    const skillsSearchResultQuery = useSearchSkillsQuery(
        searchTerm,
        ['hard', 'soft', 'language'],
        skillLimitWithPreFetchingNextItems,
    );
    const hasMoreSkills = (skillsSearchResultQuery.data ?? []).length > skillSearchLimit;
    const skills = (skillsSearchResultQuery.data ?? []).slice(0, skillSearchLimit);
    const hasResults = is.array(skillsSearchResultQuery.data);
    const isFetching = skillsSearchResultQuery.isFetching;

    return {
        hasMoreSkills,
        hasResults,
        isFetching,
        skills,
    };
};

export const useExcludeSkills = (
    skillsSet: readonly SkillShort[],
    excludedSkillIds: readonly string[],
): {
    readonly filteredSkills: readonly SkillShort[];
} => {
    const filteredSkills = useMemo(
        () => skillsSet.filter((skill) => !excludedSkillIds.some((excludedSkillId) => excludedSkillId === skill.id)),
        [excludedSkillIds, skillsSet],
    );

    return { filteredSkills };
};
